import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CreditCard, Loader2, Check, ArrowRight, MessageCircle, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { Footer } from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatIDR = (amount) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

export default function CustomPaymentPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const paymentId = searchParams.get('id');

  // If we have a session_id, show success
  if (sessionId) {
    return <CustomPaymentSuccess sessionId={sessionId} paymentId={paymentId} />;
  }

  return <CustomPaymentForm />;
}

function CustomPaymentForm() {
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const numericAmount = parseFloat(amount.replace(/[^0-9]/g, '')) || 0;

  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, '');
    setAmount(raw);
  };

  const handleSubmit = async () => {
    if (numericAmount < 10000) {
      toast.error('Minimum amount is Rp 10,000');
      return;
    }
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Please enter a valid email');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/custom-checkout/session`, {
        amount: numericAmount,
        customer_name: name,
        customer_email: email,
        description: description || 'Custom payment',
        origin_url: originUrl
      });

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error(error.response?.data?.detail || 'Error creating payment session');
      setLoading(false);
    }
  };

  const presetAmounts = [500000, 1000000, 2000000, 5000000];

  return (
    <div className="min-h-screen pt-20" data-testid="custom-payment-page">
      <div className="py-24 md:py-32 px-6 md:px-12 min-h-screen">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl tracking-tighter leading-[0.9] font-medium font-heading mb-6">
              Custom <span className="text-primary">Payment</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed tracking-wide font-light text-foreground-muted">
              Enter the amount and pay securely via Stripe
            </p>
          </motion.div>

          {/* Form Container - Glass Morphism */}
          <div
            className="p-8 md:p-12"
            style={{
              background: 'rgba(20, 20, 22, 0.7)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(234, 224, 213, 0.1)'
            }}
          >
            <div className="space-y-6">
              {/* Amount Input */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-3 block">
                  Amount (IDR) <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-foreground-muted font-semibold">Rp</span>
                  <input
                    type="text"
                    value={numericAmount > 0 ? numericAmount.toLocaleString('id-ID') : ''}
                    onChange={handleAmountChange}
                    className="w-full pl-12 pr-4 py-4 bg-background border-2 border-border text-foreground text-2xl font-heading font-bold focus:border-primary focus:outline-none transition-colors duration-200"
                    placeholder="0"
                    data-testid="custom-amount-input"
                  />
                </div>
              </div>

              {/* Preset Amounts */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {presetAmounts.map((preset) => (
                  <button
                    key={preset}
                    onClick={() => setAmount(String(preset))}
                    className={`py-3 border-2 text-sm font-semibold transition-all ${
                      numericAmount === preset
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50 text-foreground-muted'
                    }`}
                    data-testid={`preset-${preset}`}
                  >
                    {formatIDR(preset)}
                  </button>
                ))}
              </div>

              {/* Name */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-3 block">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 bg-background border-2 border-border text-foreground focus:border-primary focus:outline-none transition-colors duration-200"
                  placeholder="John Doe"
                  data-testid="custom-name-input"
                />
              </div>

              {/* Email */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-3 block">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-background border-2 border-border text-foreground focus:border-primary focus:outline-none transition-colors duration-200"
                  placeholder="john.doe@example.com"
                  data-testid="custom-email-input"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-sm font-semibold text-foreground mb-3 block">
                  Payment Description (optional)
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-4 py-3 bg-background border-2 border-border text-foreground focus:border-primary focus:outline-none transition-colors duration-200"
                  placeholder="e.g., Remaining balance, extra service..."
                  data-testid="custom-description-input"
                />
              </div>

              {/* Summary */}
              {numericAmount > 0 && (
                <div className="p-6 bg-primary/5 border border-primary/20">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-heading font-semibold">Total to pay</span>
                    <span className="text-3xl font-heading font-bold text-primary" data-testid="custom-total">
                      {formatIDR(numericAmount)}
                    </span>
                  </div>
                </div>
              )}

              {/* Security Badge */}
              <div className="flex items-center justify-center gap-3 text-foreground-muted text-sm">
                <Shield className="w-4 h-4" />
                <span>Secure payment powered by Stripe</span>
                <div className="flex items-center gap-1">
                  <img src="https://cdn-icons-png.flaticon.com/512/349/349221.png" alt="Visa" className="h-5 w-auto opacity-60" />
                  <img src="https://cdn-icons-png.flaticon.com/512/349/349228.png" alt="Mastercard" className="h-5 w-auto opacity-60" />
                </div>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={loading || numericAmount < 10000}
                className="w-full py-4 bg-primary text-primary-foreground font-body font-semibold text-lg tracking-wide hover:scale-[0.99] transition-transform duration-200 disabled:opacity-50 flex items-center justify-center gap-3"
                data-testid="custom-pay-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Redirecting to Stripe...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    {numericAmount >= 10000 ? `Pay ${formatIDR(numericAmount)}` : 'Enter an amount'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function CustomPaymentSuccess({ sessionId, paymentId }) {
  const [paymentStatus, setPaymentStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/custom-checkout/status/${sessionId}`);
        setPaymentStatus(res.data.payment_status);
        setPaymentData(res.data);
      } catch (err) {
        console.error('Error checking status:', err);
        setPaymentStatus('error');
      }
    };
    checkStatus();
  }, [sessionId]);

  const isPaid = paymentStatus === 'paid';

  return (
    <div className="min-h-screen pt-20" data-testid="custom-payment-success">
      <div className="py-24 md:py-32 px-6 flex items-center justify-center min-h-[80vh]">
        <div className="max-w-xl mx-auto text-center">
          {paymentStatus === 'loading' ? (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
              <p className="text-foreground-muted">Verifying your payment...</p>
            </div>
          ) : (
            <>
              <div className={`w-20 h-20 mx-auto mb-8 rounded-full flex items-center justify-center ${
                isPaid ? 'bg-green-500/20' : 'bg-amber-500/20'
              }`}>
                {isPaid ? (
                  <Check className="w-10 h-10 text-green-400" />
                ) : (
                  <CreditCard className="w-10 h-10 text-amber-400" />
                )}
              </div>

              <h1 className="font-heading text-4xl md:text-5xl text-foreground mb-4">
                {isPaid ? 'Payment Successful!' : 'Payment Processing'}
              </h1>

              <p className="text-foreground-muted text-lg mb-8">
                {isPaid
                  ? 'Your payment has been received. Thank you!'
                  : 'Your payment is being processed. You will receive confirmation shortly.'}
              </p>

              {paymentData?.amount_total && (
                <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/10 border border-green-500/30 text-green-400 font-heading font-bold text-2xl mb-8">
                  <CreditCard className="w-5 h-5" />
                  {formatIDR(paymentData.amount_total / 100)}
                </div>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
                <a
                  href="https://wa.me/628133975057"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white font-semibold hover:bg-[#25D366]/90 transition-colors"
                >
                  <MessageCircle className="w-5 h-5" />
                  WhatsApp Us
                </a>
              </div>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/custom-payment"
                  className="text-foreground-muted hover:text-primary transition-colors flex items-center gap-2"
                >
                  Make Another Payment <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/"
                  className="text-foreground-muted hover:text-primary transition-colors flex items-center gap-2"
                >
                  Back to Home <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
